import * as React from 'react';
import {useSelector} from 'react-redux';

import Mousetrap from 'mousetrap';

import {Session} from 'nutshell-core/session';
import type {User} from 'nutshell-core/types';

import {NavIconSalesIcon} from 'shells/icon/navigation/nav-icon-sales';
import {NavIconSettingsIcon} from 'shells/icon/navigation/nav-icon-settings';
import {AiSparkleNewIcon} from 'shells/icon/svg/ai-sparkle-new-icon';
import {LeadIcon} from 'shells/icon/svg/lead-icon';
// @ts-expect-error - imported from .js/.jsx file
import {OutreachIcon} from 'shells/icon/svg/outreach-icon';
// @ts-expect-error - imported from .js/.jsx file
import {SidebarIconAccountsIcon} from 'shells/icon/svg/sidebar-icon-accounts';
// @ts-expect-error - imported from .js/.jsx file
import {SidebarIconContactsIcon} from 'shells/icon/svg/sidebar-icon-contacts';
// @ts-expect-error - imported from .js/.jsx file
import {SidebarIconReportsIcon} from 'shells/icon/svg/sidebar-icon-reports';

import {EntityList} from '../../../entity-list';
import type {NutshellState} from '../../../store';

import {useGetSessionUser} from '../../core/use-get-session-user';
import {OnboardingChecklistButton} from '../../onboarding/onboarding-checklist/onboarding-checklist-button';
import {userCanAccessSetup} from '../../setup/helpers';
import {AnimatingSidebarDrawer} from '../../sidebar/animating-sidebar-drawer';

import {CollapseSidebarButton} from './collapse-sidebar-button';
import {EngagementSidebarItem} from './engagement-sidebar-item';
import {ExploreNavItem} from './explore-nav-item';
import {NutshellHome} from './nutshell-home';
import {SidebarIcon} from './sidebar-icon';
import {SidebarNavLink} from './sidebar-nav-link';
import {useSidebarCollapse} from './use-sidebar-collapse';

// Our icons unfortunately aren't all uniform, and some look better at slightly
// bigger/smaller sizes in comparison to others. This function serves to unify
// their appearances so they all look the same size
export function getIconSize(key: string) {
    switch (key) {
        case 'explore':
        case 'ai':
            return 20;
        case 'accounts':
        case 'people':
        case 'leads':
        case 'reports':
        case 'sales':
        case 'marketing':
        case 'setup':
            return 17;
        case 'engagement':
        case 'help':
        default:
            return 15;
    }
}

export const ICON_SIZE = 24;

type ActiveTab = string; // 'sales' | 'marketing' | 'engagement' | 'accounts' | 'contacts' | 'leads' | 'reports' | 'setup' | 'ai' | 'explore';

export type SidebarDrawerSection =
    | 'search'
    | 'create-new'
    | 'notifications'
    | 'user'
    | 'calendar'
    | 'onboarding';

type Props = {
    activeTab: ActiveTab;
};

export function Sidebar(props: Props) {
    const [isClosingSidebarDrawer, setIsClosingSidebarDrawer] = React.useState(false);
    const [isOpeningSidebarDrawer, setIsOpeningSidebarDrawer] = React.useState(false);
    const [activeSidebarDrawer, setActiveSidebarDrawer] = React.useState<any>(null);
    const sidebarNodeRef = React.useRef(null);
    const {activeTab} = props;
    const setSidebarCollapsed = useSidebarCollapse();

    const loggedInUser: User | null | undefined = useSelector((state: NutshellState) =>
        Session.getSessionStateUser(state)
    );
    const persistedLeadsParams = useSelector((state: NutshellState) =>
        EntityList.getPersistedLeadsParams(state)
    );
    const persistedAccountsParams = useSelector((state: NutshellState) =>
        EntityList.getPersistedAccountsParams(state)
    );
    const persistedContactsParams = useSelector((state: NutshellState) =>
        EntityList.getPersistedContactsParams(state)
    );

    const {user} = useGetSessionUser();

    const handleDrawerOpen = () => {
        setIsOpeningSidebarDrawer(false);
    };

    // Called when _this_ component triggers a close. We don't want to immediately
    // clear our activeSidebarDrawer state, because it will unmount the drawer
    // and not allow it's animate-out animation to run.
    //
    // Instead, we'll flip a bit that we pass to the drawer, which then triggers
    // _its_ closing animation, which will eventually call `clearActiveSidebarDrawer`
    // in this componnet
    const startClosingSidebarDrawer = () => {
        setIsClosingSidebarDrawer(true);
    };

    // Actually unmounts the active sidebar drawer, and reset our isClosing state
    const clearActiveSidebarDrawer = () => {
        setActiveSidebarDrawer(null);
        setIsClosingSidebarDrawer(false);
    };

    const toggleSidebarDrawerTab =
        (type: SidebarDrawerSection) => (e?: React.SyntheticEvent | React.KeyboardEvent) => {
            if (e) {
                e.stopPropagation();
            }
            // @ts-expect-error
            if (e && e.key && !(e.key === 'Enter' || e.key === ' ')) {
                return;
            }

            if (activeSidebarDrawer === type) {
                startClosingSidebarDrawer();
            } else {
                setActiveSidebarDrawer(type);
                setIsOpeningSidebarDrawer(true);
            }
        };

    // close the drawer with escape key
    React.useEffect(() => {
        const keydownEventFunction = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                startClosingSidebarDrawer();
            }
        };
        document.addEventListener('keydown', keydownEventFunction);

        const openOnboarding = () => {
            setActiveSidebarDrawer('onboarding');
        };
        window.addEventListener('openOnboarding', openOnboarding);

        return () => {
            document.removeEventListener('keydown', keydownEventFunction);
            window.removeEventListener('openOnboarding', openOnboarding);
        };
    }, []);

    const basePath = window.location.pathname.split('/')[1];
    const canUseOrTryInbox = loggedInUser && loggedInUser.permissions.canUseOrTryInbox;
    const inboxUrl = canUseOrTryInbox ? '/inbox' : '/inbox/no-access';

    const collapseBreakpoint = activeTab === 'engagement' ? 1440 : 1024;
    const prevBreakpointRef = React.useRef(collapseBreakpoint);

    React.useEffect(() => {
        if (!loggedInUser) return;

        // Only apply collapse state when the breakpoint changes (route navigation),
        // not on every render — to avoid overriding manual user toggles.
        if (prevBreakpointRef.current !== collapseBreakpoint) {
            prevBreakpointRef.current = collapseBreakpoint;
            const shouldCollapse = window.innerWidth < collapseBreakpoint;
            setSidebarCollapsed(loggedInUser.id, shouldCollapse);
        }

        let lastWidth = window.innerWidth;

        const handleResize = () => {
            if (!loggedInUser) return;

            const currentWidth = window.innerWidth;
            const shouldCollapse = currentWidth < collapseBreakpoint;

            if (
                (lastWidth >= collapseBreakpoint && currentWidth < collapseBreakpoint) ||
                (lastWidth < collapseBreakpoint && currentWidth >= collapseBreakpoint)
            ) {
                setSidebarCollapsed(loggedInUser.id, shouldCollapse);
            }

            lastWidth = currentWidth;
        };

        window.addEventListener('resize', handleResize);
        handleResize();

        return () => window.removeEventListener('resize', handleResize);
    }, [loggedInUser, setSidebarCollapsed, collapseBreakpoint]);

    const isMainSidebarCollapsed = Boolean(loggedInUser && loggedInUser.isMainSidebarCollapsed);

    React.useEffect(() => {
        if (user && user.hasDisabledShortcuts) {
            return;
        }

        Mousetrap.bind(['ctrl+/', 'meta+/'], (e) => {
            e.preventDefault();
            if (!loggedInUser) {
                return;
            }
            setSidebarCollapsed(loggedInUser.id, !isMainSidebarCollapsed);
        });

        return () => {
            Mousetrap.unbind(['ctrl+/', 'meta+/']);
        };
    }, [loggedInUser, isMainSidebarCollapsed, setSidebarCollapsed, user]);

    const canAccessCrm = !loggedInUser || (loggedInUser && loggedInUser.permissions.canAccessCrm);

    return (
        <div className='nut-sidebar--container' ref={sidebarNodeRef}>
            <div className='nut-sidebar--nav-icons'>
                <NutshellHome loggedInUser={loggedInUser} isCollapsed={isMainSidebarCollapsed} />
                {NutClientConfig.hasTrialOnboarding && NutClientConfig.isTrial && (
                    <ExploreNavItem
                        activeTab={activeTab}
                        // @ts-expect-error
                        basePath={basePath}
                        isMainSidebarCollapsed={isMainSidebarCollapsed}
                        shouldShowExploreAttentionBadge={
                            loggedInUser?.shouldShowExploreAttentionBadge
                        }
                    />
                )}
                {canAccessCrm && (
                    <SidebarNavLink
                        isActive={activeTab === 'sales'}
                        onBasePath={basePath === 'sales'}
                        href='/sales/dashboard'
                        to='/sales/dashboard'
                        tooltipText={isMainSidebarCollapsed ? 'Sales' : undefined}
                        intercomKey='sales'
                    >
                        <SidebarIcon
                            icon={NavIconSalesIcon}
                            size={getIconSize('sales')}
                            label='Sales'
                        />
                    </SidebarNavLink>
                )}
                <SidebarNavLink
                    intercomKey='marketing'
                    isActive={activeTab === 'marketing'}
                    onBasePath={basePath === 'marketing'}
                    href='/marketing/dashboard'
                    to='/marketing/dashboard'
                    tooltipText={isMainSidebarCollapsed ? 'Marketing' : undefined}
                >
                    <SidebarIcon
                        icon={OutreachIcon}
                        size={getIconSize('marketing')}
                        label='Marketing'
                    />
                </SidebarNavLink>
                <EngagementSidebarItem
                    inboxUrl={inboxUrl}
                    isActiveTab={activeTab === 'engagement'}
                    isMainSidebarCollapsed={isMainSidebarCollapsed}
                    loggedInUser={loggedInUser}
                />
                <div className='nut-sidebar--divider' />
                <SidebarNavLink
                    intercomKey='accounts'
                    isActive={activeTab === 'accounts'}
                    onBasePath={basePath === 'companies'}
                    href='/companies'
                    to={`/companies${persistedAccountsParams ? `?${persistedAccountsParams}` : ''}`}
                    tooltipText={isMainSidebarCollapsed ? 'Companies' : undefined}
                >
                    <SidebarIcon
                        icon={SidebarIconAccountsIcon}
                        size={getIconSize('accounts')}
                        label='Companies'
                    />
                </SidebarNavLink>
                <SidebarNavLink
                    intercomKey='contacts'
                    isActive={activeTab === 'contacts'}
                    onBasePath={basePath === 'people'}
                    href='/people'
                    to={`/people${persistedContactsParams ? `?${persistedContactsParams}` : ''}`}
                    tooltipText={isMainSidebarCollapsed ? 'People' : undefined}
                >
                    <SidebarIcon
                        icon={SidebarIconContactsIcon}
                        size={getIconSize('people')}
                        label='People'
                    />
                </SidebarNavLink>
                <SidebarNavLink
                    intercomKey='leads'
                    isActive={activeTab === 'leads'}
                    onBasePath={basePath === 'leads'}
                    href='/leads'
                    to={`/leads${persistedLeadsParams ? `?${persistedLeadsParams}` : ''}`}
                    tooltipText={isMainSidebarCollapsed ? 'Leads' : undefined}
                >
                    <SidebarIcon icon={LeadIcon} size={getIconSize('leads')} label='Leads' />
                </SidebarNavLink>
                {loggedInUser && loggedInUser.permissions.canAccessCrm && (
                    <SidebarNavLink
                        intercomKey='reports'
                        isActive={activeTab === 'reports'}
                        onBasePath={basePath === 'reports'}
                        href='/reports'
                        to={
                            NutClientConfig.hasReportGeneratorFF
                                ? '/reports/overview'
                                : '/reports/sales/details'
                        }
                        tooltipText={isMainSidebarCollapsed ? 'Reports' : undefined}
                    >
                        <SidebarIcon
                            icon={SidebarIconReportsIcon}
                            size={getIconSize('reports')}
                            label='Reports'
                        />
                    </SidebarNavLink>
                )}
                {NutClientConfig.hasAiHomepageFF && (
                    <>
                        <div className='nut-sidebar--divider' />
                        <SidebarNavLink
                            isActive={activeTab === 'ai'}
                            onBasePath={basePath === 'ai'}
                            href='/ai/home'
                            to='/ai/home'
                            variant='ai'
                        >
                            <SidebarIcon
                                icon={AiSparkleNewIcon}
                                size={getIconSize('ai')}
                                label='Nutshell AI'
                            />
                        </SidebarNavLink>
                    </>
                )}
            </div>
            <div className='nut-sidebar--bottom-section'>
                {NutClientConfig.shouldShowOnboardingSidebar && (
                    <OnboardingChecklistButton
                        onToggleSidebar={toggleSidebarDrawerTab('onboarding')}
                        isCollapsed={isMainSidebarCollapsed}
                    />
                )}
                <CollapseSidebarButton
                    userId={loggedInUser && loggedInUser.id}
                    isSidebarCollapsed={isMainSidebarCollapsed}
                />
                {NutClientConfig.hasTrialOnboarding && !NutClientConfig.isTrial && (
                    <ExploreNavItem
                        activeTab={activeTab}
                        // @ts-expect-error
                        basePath={basePath}
                        isMainSidebarCollapsed={isMainSidebarCollapsed}
                        shouldShowExploreAttentionBadge={
                            loggedInUser?.shouldShowExploreAttentionBadge
                        }
                    />
                )}
                {userCanAccessSetup(loggedInUser && loggedInUser.permissions) && (
                    <SidebarNavLink
                        isActive={activeTab === 'manage'}
                        intercomKey='settings'
                        onBasePath={basePath === 'setup'}
                        href={
                            loggedInUser && loggedInUser.permissions.canAccessSetup
                                ? '/setup/index'
                                : '/my-account/settings'
                        }
                        to={
                            loggedInUser && loggedInUser.permissions.canAccessSetup
                                ? '/setup/index'
                                : '/my-account/settings'
                        }
                        tooltipText={
                            loggedInUser && loggedInUser.isMainSidebarCollapsed
                                ? 'Settings'
                                : undefined
                        }
                    >
                        <SidebarIcon
                            icon={NavIconSettingsIcon}
                            size={getIconSize('setup')}
                            label='Settings'
                        />
                    </SidebarNavLink>
                )}
            </div>
            <AnimatingSidebarDrawer
                activeSection={activeSidebarDrawer}
                onClose={clearActiveSidebarDrawer}
                onOpen={handleDrawerOpen}
                isClosing={isClosingSidebarDrawer}
                isOpening={isOpeningSidebarDrawer}
                closeSidebar={startClosingSidebarDrawer}
                isShortSidebar={false}
                user={loggedInUser}
                isSidebarCollapsed={isMainSidebarCollapsed}
            />
        </div>
    );
}
